import React, { useCallback, useEffect, useState } from 'react';
import { FaMoon, FaSun, FaDesktop, FaMagic, FaFilm, FaEye, FaBan } from 'react-icons/fa';
import {
  DEFAULT_APPEARANCE_PREFS,
  loadAppearancePrefs,
  type AppearancePrefs,
  type UiDensity,
  type UiTheme,
} from '../utils/appearancePrefs.ts';
import { applyAppearanceToDocument, persistAppearancePrefs } from '../utils/appearanceTheme.ts';

export type { AppearancePrefs, UiDensity, UiTheme } from '../utils/appearancePrefs.ts';
export { DEFAULT_APPEARANCE_PREFS, loadAppearancePrefs } from '../utils/appearancePrefs.ts';
export { applyAppearanceToDocument } from '../utils/appearanceTheme.ts';

function ToggleSwitch({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      className={`settings-notif-toggle${checked ? ' is-on' : ''}`}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
    >
      <span className="settings-notif-toggle__knob" />
    </button>
  );
}

type Props = {
  userId: number;
  t: (key: string) => string;
};

export function AppearanceSettings({ userId, t }: Props) {
  const [prefs, setPrefs] = useState<AppearancePrefs>(() => loadAppearancePrefs(userId));
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    setPrefs(loadAppearancePrefs(userId));
  }, [userId]);

  useEffect(() => {
    applyAppearanceToDocument(prefs);
  }, [prefs.theme, prefs.density, prefs.reduceMotion]);

  const handleThemeSelect = useCallback(
    (theme: UiTheme) => {
      setPrefs((prev) => {
        const next = { ...prev, theme };
        persistAppearancePrefs(userId, next);
        return next;
      });
    },
    [userId],
  );

  const handleSave = useCallback(() => {
    persistAppearancePrefs(userId, prefs);
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 2000);
  }, [userId, prefs]);

  const handleReset = () => {
    const next = { ...DEFAULT_APPEARANCE_PREFS };
    setPrefs(next);
    persistAppearancePrefs(userId, next);
  };

  const themeCards: { id: UiTheme; icon: React.ReactNode; titleKey: string; descKey: string }[] = [
    { id: 'dark', icon: <FaMoon />, titleKey: 'settings.appearThemeDark', descKey: 'settings.appearThemeDarkDesc' },
    { id: 'light', icon: <FaSun />, titleKey: 'settings.appearThemeLight', descKey: 'settings.appearThemeLightDesc' },
    { id: 'system', icon: <FaDesktop />, titleKey: 'settings.appearThemeSystem', descKey: 'settings.appearThemeSystemDesc' },
  ];

  const densityCards: { id: UiDensity; titleKey: string; descKey: string }[] = [
    { id: 'compact', titleKey: 'settings.appearDensityCompact', descKey: 'settings.appearDensityCompactDesc' },
    { id: 'standard', titleKey: 'settings.appearDensityStandard', descKey: 'settings.appearDensityStandardDesc' },
    { id: 'comfortable', titleKey: 'settings.appearDensityComfortable', descKey: 'settings.appearDensityComfortableDesc' },
  ];

  const effectRows = [
    { key: 'softGlow' as const, icon: <FaMagic />, titleKey: 'settings.appearEffectGlow', descKey: 'settings.appearEffectGlowDesc' },
    { key: 'animations' as const, icon: <FaFilm />, titleKey: 'settings.appearEffectAnim', descKey: 'settings.appearEffectAnimDesc' },
    { key: 'cardTransparency' as const, icon: <FaEye />, titleKey: 'settings.appearEffectGlass', descKey: 'settings.appearEffectGlassDesc' },
    { key: 'reduceMotion' as const, icon: <FaBan />, titleKey: 'settings.appearEffectReduce', descKey: 'settings.appearEffectReduceDesc' },
  ];

  return (
    <div className="settings-container settings-appear">
      <h1 className="settings-title">{t('settings.sectionAppearance')}</h1>
      <p className="settings-subtitle">{t('settings.appearIntro')}</p>
      {savedFlash && <div className="alert alert-success">{t('settings.appearSaved')}</div>}

      <section className="settings-appear-block">
        <h2 className="settings-appear-block__title">{t('settings.appearThemeTitle')}</h2>
        <div className="settings-appear-cards">
          {themeCards.map((card) => (
            <button
              key={card.id}
              type="button"
              className={`settings-appear-card settings-appear-card--theme${prefs.theme === card.id ? ' is-selected' : ''}`}
              onClick={() => handleThemeSelect(card.id)}
            >
              <span className="settings-appear-card__radio" aria-hidden />
              <div className={`settings-appear-preview-ui settings-appear-preview-ui--${card.id}`} aria-hidden>
                <div className="settings-appear-preview-ui__bar" />
                <div className="settings-appear-preview-ui__body">
                  <div className="settings-appear-preview-ui__side" />
                  <div className="settings-appear-preview-ui__main">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
              <span className="settings-appear-card__icon">{card.icon}</span>
              <span className="settings-appear-card__title">{t(card.titleKey)}</span>
              <span className="settings-appear-card__desc">{t(card.descKey)}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="settings-appear-block">
        <h2 className="settings-appear-block__title">{t('settings.appearDensityTitle')}</h2>
        <div className="settings-appear-cards">
          {densityCards.map((card) => (
            <button
              key={card.id}
              type="button"
              className={`settings-appear-card${prefs.density === card.id ? ' is-selected' : ''}`}
              onClick={() => setPrefs((p) => ({ ...p, density: card.id }))}
            >
              <span className="settings-appear-card__radio" aria-hidden />
              <div className={`settings-appear-density-bars settings-appear-density-bars--${card.id}`} aria-hidden>
                <span /><span /><span />
              </div>
              <span className="settings-appear-card__title">{t(card.titleKey)}</span>
              <span className="settings-appear-card__desc">{t(card.descKey)}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="settings-appear-block">
        <h2 className="settings-appear-block__title">{t('settings.appearEffectsTitle')}</h2>
        <div className="settings-appear-split">
          <ul className="settings-appear-effects">
            {effectRows.map((row) => (
              <li key={row.key} className="settings-notif-row settings-appear-effect-row">
                <span className="settings-notif-row__icon" aria-hidden>{row.icon}</span>
                <div className="settings-notif-row__text">
                  <span className="settings-notif-row__title">{t(row.titleKey)}</span>
                  <span className="settings-notif-row__desc">{t(row.descKey)}</span>
                </div>
                <ToggleSwitch
                  checked={prefs[row.key]}
                  onChange={(v) => setPrefs((p) => ({ ...p, [row.key]: v }))}
                  ariaLabel={t(row.titleKey)}
                />
              </li>
            ))}
          </ul>
          <div className="settings-appear-preview-panel">
            <span className="settings-appear-preview-panel__label">{t('settings.appearPreview')}</span>
            <div
              className={`settings-appear-preview-card${prefs.softGlow ? ' has-glow' : ''}${prefs.cardTransparency ? ' is-glass' : ''}`}
            >
              <button type="button" className="settings-appear-preview-btn primary">
                {t('settings.appearPreviewPrimary')}
              </button>
              <button type="button" className="settings-appear-preview-btn secondary">
                {t('settings.appearPreviewSecondary')}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="settings-appear-block">
        <h2 className="settings-appear-block__title">{t('settings.appearEditorTitle')}</h2>
        <div className="settings-appear-split">
          <div className="settings-appear-editor-controls">
            <div className="settings-field">
              <label className="settings-field-label" htmlFor="appear-font-size">
                {t('settings.appearFontSize')}
              </label>
              <div className="settings-appear-slider-row">
                <input
                  id="appear-font-size"
                  type="range"
                  min={12}
                  max={20}
                  step={1}
                  value={prefs.editorFontSize}
                  onChange={(e) => setPrefs((p) => ({ ...p, editorFontSize: Number(e.target.value) }))}
                  className="settings-appear-slider"
                />
                <span className="settings-appear-slider-value">{prefs.editorFontSize}px</span>
              </div>
            </div>
            <div className="settings-field">
              <label className="settings-field-label" htmlFor="appear-line-height">
                {t('settings.appearLineHeight')}
              </label>
              <div className="settings-appear-slider-row">
                <input
                  id="appear-line-height"
                  type="range"
                  min={1.2}
                  max={2}
                  step={0.1}
                  value={prefs.editorLineHeight}
                  onChange={(e) => setPrefs((p) => ({ ...p, editorLineHeight: Number(e.target.value) }))}
                  className="settings-appear-slider"
                />
                <span className="settings-appear-slider-value">{prefs.editorLineHeight.toFixed(1)}</span>
              </div>
            </div>
            <div className="settings-field">
              <label className="settings-field-label" htmlFor="appear-editor-theme">
                {t('settings.appearSyntaxTheme')}
              </label>
              <select
                id="appear-editor-theme"
                className="settings-input settings-select"
                value={prefs.editorTheme}
                onChange={(e) => setPrefs((p) => ({ ...p, editorTheme: e.target.value }))}
              >
                <option value="sciplex-dark">{t('settings.appearSyntaxSciplexDark')}</option>
                <option value="sciplex-light">{t('settings.appearSyntaxSciplexLight')}</option>
                <option value="monokai">Monokai</option>
              </select>
            </div>
            <ul className="settings-appear-effects">
              <li className="settings-notif-row settings-appear-effect-row settings-appear-effect-row--no-icon">
                <div className="settings-notif-row__text">
                  <span className="settings-notif-row__title">{t('settings.appearMinimap')}</span>
                  <span className="settings-notif-row__desc">{t('settings.appearMinimapDesc')}</span>
                </div>
                <ToggleSwitch
                  checked={prefs.showMinimap}
                  onChange={(v) => setPrefs((p) => ({ ...p, showMinimap: v }))}
                  ariaLabel={t('settings.appearMinimap')}
                />
              </li>
              <li className="settings-notif-row settings-appear-effect-row settings-appear-effect-row--no-icon">
                <div className="settings-notif-row__text">
                  <span className="settings-notif-row__title">{t('settings.appearActiveLine')}</span>
                  <span className="settings-notif-row__desc">{t('settings.appearActiveLineDesc')}</span>
                </div>
                <ToggleSwitch
                  checked={prefs.highlightActiveLine}
                  onChange={(v) => setPrefs((p) => ({ ...p, highlightActiveLine: v }))}
                  ariaLabel={t('settings.appearActiveLine')}
                />
              </li>
            </ul>
          </div>
          <div className="settings-appear-preview-panel">
            <span className="settings-appear-preview-panel__label">{t('settings.appearEditorPreview')}</span>
            <pre
              className={`settings-appear-code-preview settings-appear-code-preview--${prefs.editorTheme}${prefs.highlightActiveLine ? ' has-active-line' : ''}`}
              style={{
                fontSize: `${prefs.editorFontSize}px`,
                lineHeight: prefs.editorLineHeight,
              }}
            >
              <code>
                <span className="tok-kw">function</span> <span className="tok-fn">fib</span>(n) {'{\n'}
                {'  '}<span className="tok-kw">if</span> (n &lt;= <span className="tok-num">1</span>) <span className="tok-kw">return</span> n;{'\n'}
                {'  '}<span className="tok-kw">return</span> <span className="tok-fn">fib</span>(n - <span className="tok-num">1</span>) + <span className="tok-fn">fib</span>(n - <span className="tok-num">2</span>);{'\n'}
                {'}'}
              </code>
              {prefs.showMinimap && <span className="settings-appear-code-preview__minimap" aria-hidden />}
            </pre>
          </div>
        </div>
      </section>

      <div className="settings-notif-actions">
        <button type="button" className="btn btn-primary" onClick={handleSave}>
          {t('settings.saveChanges')}
        </button>
        <button type="button" className="btn btn-secondary" onClick={handleReset}>
          {t('settings.appearReset')}
        </button>
      </div>
    </div>
  );
}
